import {
  BpPlacementComponentConstructor,
  BpDomElement,
} from '../../models/component.model';
import { EpLocator } from '../../util/locator';
import { lookupPlacementConstructor } from '../../util/constructor-lookup/placement-constructor-lookup.util';
import { EpLogger } from '../../util/ep-logger';
import { BaseTextPlacementComponent } from '../../components/base-text-placement/base-text-placement.component';
import { Analytics } from '../../modules/analytics/analytics';
import { autoInjectable } from 'tsyringe';
import { GeneratePlacementResponse, RequestPlacement } from '@ads-ep/fetch-api-client';
import { PlacementConfig } from '../../models/placement-config.model';
import { EpApi } from '../../modules/api/ep-api';
import { ConfigUtilService } from '../config/config-util.service';
import { assignDefined } from '../../models/util.model';

@autoInjectable()
export class ComponentGenerator {
  get config(){
    return this.configUtilService.getConfig();
  }
  static epDomSelector = '[data-ep-placement]';

  constructor(private logger: EpLogger,
              private analytics?: Analytics,
              private epApi?: EpApi,
              private configUtilService?: ConfigUtilService) {
  }

  /*
   * Create and instance of the component and run the init method.
   * This will typically involve compiling a template, adding it
   * to the dom, and binding all necessary event handlers.
   * */
  createComponent<T extends BaseTextPlacementComponent>(
    componentType: BpPlacementComponentConstructor<T>,
    target: HTMLElement,
    placementConfig: PlacementConfig,
    brandPresentmentConfig: GeneratePlacementResponse,
  ): T {
    let newCmp: T;

    try {
      newCmp = new componentType(target, placementConfig, brandPresentmentConfig);
      newCmp.init();
    } catch (e) {
      console.error(e);
    }

    return newCmp;
  }

  /*
   * Return the component constructor from the placement config
   * */

  // TODO write test fo make sure undefined works
  getComponentConstructor(
    placementConfig: PlacementConfig,
  ): BpPlacementComponentConstructor<BaseTextPlacementComponent> | undefined {
    if (!placementConfig || !placementConfig.content) {
      return undefined;
    }
    const placementClass = placementConfig.content.contentType;

    return lookupPlacementConstructor(placementClass);
  }

  /*
   * Get all of the dom elements that EpJs will target
   * */
  getBpDomElements(): BpDomElement[] {
    return EpLocator.queryAll(
      ComponentGenerator.epDomSelector,
    ) as BpDomElement[];
  }

  /*
   * Find all the targeted dom elements and replace them with
   * EpJs elements based on the predefined constructors.
   * - Find all dom elements
   * - Make single request for all placement configs
   * - Generate each placement from config response
   * */
  async generateAll() {
    const bpDomElements = this.getBpDomElements();
    const placementConfigs = await this.fetchPlacements(bpDomElements);

    for (const domEl of bpDomElements) {
      this.generateTargetComponent(domEl, placementConfigs);
    }
  }

  async fetchPlacements(bpDomElements: BpDomElement[], dataIdAttr?: string) {
    const placementsToRequest: RequestPlacement[] = [];
    const cmpId = this.analytics.getCmpId();

    bpDomElements.forEach((domEl) => {
      const id = this.getDomPlacementId(domEl, dataIdAttr);
      let context: any = assignDefined({},{
        SDK_TID: this.analytics.userTrackingId,
        ENV: this.config.env,
        CMP: cmpId,
      });
      if (!!domEl.dataset.amount) {
        context = {
          ...context,
          PRICE: parseInt(domEl.dataset.amount, 10),
        };
      }

      if (!!domEl.dataset.storeNumber) {
        context = {
          ...context,
          STORE_NUMBER: domEl.dataset.storeNumber,
        };
      }

      if (!!domEl.dataset.location) {
        context = {
          ...context,
          LOCATION: domEl.dataset.location,
        };
      }

      if (!!domEl.dataset.loyaltyId) {
        context = {
          ...context,
          LOYALTY_ID: domEl.dataset.loyaltyId,
        };
      }

      if (!!domEl.dataset.existingCardholder) {
        context = {
          ...context,
          EXISTING_CH: domEl.dataset.existingCardholder,
        };
      }

      if (!!domEl.dataset.cardholderTier) {
        context = {
          ...context,
          CARDHOLDER_TIER: domEl.dataset.cardholderTier,
        };
      }

      if (!!domEl.dataset.rtpsId) {
        context = {
          ...context,
          RTPS_ID: domEl.dataset.rtpsId,
        };
      }

      if (!!domEl.dataset.sku) {
        context = {
          ...context,
          SKU: domEl.dataset.sku,
        };
      }

      if (!!domEl.dataset.productCategory) {
        context = {
          ...context,
          PRODUCT_CATEGORY: domEl.dataset.productCategory,
        };
      }

      /**
       * Use stored attribution if it is available
       * */
      const attributionParams = this.analytics.getSessionAttribution();
      context = assignDefined({}, context, attributionParams);

      return placementsToRequest.push({ id, context });
    });
    if (placementsToRequest.length > 0) {
      return this.epApi.generatePlacements({
        placements: placementsToRequest,
      });
    }
  }

  /*
  * TODO Add tests
  * */
  getPlacementConfig(placementId: string, brandPresentmentConfig: GeneratePlacementResponse): PlacementConfig {
    const placementConfig: PlacementConfig = new PlacementConfig();

    /*
    * Find the placement that matches the given id
    * */
    const basePlacementConfig = brandPresentmentConfig.placements.filter(placement => placement.id === placementId)[0];
    if (basePlacementConfig) {
      placementConfig.id = basePlacementConfig.id;
    } else {
      return placementConfig;
    }

    /*
    * Pick random placement content
    * */
    if (!!basePlacementConfig.content) {
      const placementContentId = basePlacementConfig.content.contentId;

      /*
      * Find the placement content that matches the placement content id
      * */
      const basePlacementContent = brandPresentmentConfig.placementContent.filter(
        placementContent => placementContent.id === placementContentId)[0];
      placementConfig.content = { ...basePlacementContent };
    }

    return placementConfig;
  }

  /*
  * Return the placement id
  * */
  getDomPlacementId(domEl: HTMLElement, dataIdAttr = 'epPlacement') {
    return domEl.dataset[dataIdAttr] ||
      domEl.dataset.epId ||
      domEl.id;
  }

  getRandomInt(max: number): number {
    return Math.floor(Math.random() * Math.floor(max));
  }

  /*
   * - Get constructor for target element
   * -
   * */
  generateTargetComponent(domEl: HTMLElement, brandPresentmentConfig: GeneratePlacementResponse) {
    const placementId = this.getDomPlacementId(domEl);
    /*
    * TODO What should happen if placement id is undefined
    * */

    /*
    * Get placement config early
    * */
    const placementConfig: PlacementConfig = this.getPlacementConfig(placementId, brandPresentmentConfig);
    const compConstructor = this.getComponentConstructor(placementConfig);
    // TODO test create comp only called if components defined
    if (!!placementConfig && !!compConstructor) {
      this.createComponent(compConstructor, domEl, placementConfig, brandPresentmentConfig);
    }
  }
}
